import { api } from '../api';
import type { Product, Category } from '@/types';

// Extended Product type matching backend response
export interface ProductWithCategories extends Omit<Product, 'category_id' | 'ingredients' | 'allergens'> {
  categories: { id: number; name: string }[];
  ingredients?: string; // Backend returns string, not array
  /**
   * Коды аллергенов через запятую ('fish,sesame'). Разбор — parseAllergens
   * из '@/lib/allergens'. До наката миграции product_allergens.sql сервер
   * отдаёт null: это «не размечено», а не «аллергенов нет».
   */
  allergens?: string | null;
  serving_size?: string;
  sku?: string;
  status: 'available' | 'unavailable' | 'hidden';
  spiciness: 'none' | 'spicy';
  is_new?: boolean;
  is_popular?: boolean;
  is_riceless?: boolean;
  is_scorched?: boolean;
  is_mini?: boolean;
  is_set_of_the_day?: boolean;
  set_of_the_day_image_desktop?: string | null;
  set_of_the_day_image_mobile?: string | null;
  set_of_the_day_video_desktop?: string | null;
  set_of_the_day_video_mobile?: string | null;
}

// API Response types
export interface ProductsResponse {
  data: ProductWithCategories[];
}

/**
 * Get all products with categories
 */
export async function getProducts(): Promise<ProductWithCategories[]> {
  const response = await api.get<ProductWithCategories[]>('/products/with_categories');
  return response.data;
}

/**
 * Get products by delivery point
 */
export async function getProductsByDeliveryPoint(
  pointId: number
): Promise<ProductWithCategories[]> {
  const response = await api.get<ProductWithCategories[]>(
    `/products/by-delivery-point/${pointId}`
  );
  return response.data;
}

/**
 * Get product by ID
 */
export async function getProductById(
  id: number,
  includeHidden = false
): Promise<ProductWithCategories> {
  const response = await api.get<ProductWithCategories>(
    `/products/${id}${includeHidden ? '?include_hidden=true' : ''}`
  );
  return response.data;
}

/**
 * Get set ingredients
 */
export async function getSetIngredients(
  setId: number,
  includeHidden = false
): Promise<ProductWithCategories[]> {
  const response = await api.get<ProductWithCategories[]>(
    `/products/set/${setId}/ingredients${includeHidden ? '?include_hidden=true' : ''}`
  );
  return response.data;
}

/**
 * Get gift rolls
 */
export async function getGiftRolls(): Promise<ProductWithCategories[]> {
  const response = await api.get<ProductWithCategories[]>('/products/gift-rolls');
  return response.data;
}

/**
 * Get all categories
 */
export async function getCategories(): Promise<Category[]> {
  const response = await api.get<Category[]>('/categories');
  return response.data;
}

/**
 * Group products by category
 */
export function groupProductsByCategory(
  products: ProductWithCategories[]
): Map<number, { category: { id: number; name: string }; products: ProductWithCategories[] }> {
  const grouped = new Map<
    number,
    { category: { id: number; name: string }; products: ProductWithCategories[] }
  >();

  products.forEach((product) => {
    product.categories.forEach((category) => {
      if (!grouped.has(category.id)) {
        grouped.set(category.id, {
          category,
          products: [],
        });
      }
      grouped.get(category.id)!.products.push(product);
    });
  });

  return grouped;
}

// filterProductsByIngredient удалён вместе с фильтрами каталога (решение владельца,
// вопрос 29 — «оставить выключенными и удалить мёртвый код»). Словарь ингредиентов
// покрывал шесть слов и на остальных давал пустую выдачу; поиск по каталогу
// (catalog.tsx) ищет по названию, описанию и составу без словаря.

